
import {
  Box,
  Grid,
  HStack,
  Image,
  Stack,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";

import ReactGA from "react-ga4";
import { Helmet } from "react-helmet";
import { useEffect, useState } from "react";

import FilteredGames from "../Components/Games/FilteredGames";
import { useGames } from "../Hooks/useGetGames";
import Carousel from "../Components/Carousel/Carousel";
import GameBannerAd from "../Components/Advertisement/GameBannerAd";
import ComicPanel from "../Components/Comics/ComicPanel";
import taktLogo from "../assets/LBPortal_Graphic_AdBG.webp";
import placeholderThumbnail from "../assets/GameThumbnail_Empty.png";

const BLOG_ID = "460807346859212205";
const API_KEY = process.env.REACT_APP_BLOGGER_API_KEY;

function slugify(text) {
  return (text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function Homepage() {
  ReactGA.send({
    hitType: "pageview",
    page: "/",
    title: "Home",
  });

  const { data, isLoading } = useGames();
  const [isLargerThan1580] = useMediaQuery(
    "(min-width: 1580px)"
  );

  if (isLoading || !data?.games) {
    return <div />;
  }

  const carouselGames = data.games.filter(
    (game) => game.is_display_in_slideshow === true
  );

  const singlePlayer = data.games.filter(
    (item) => !item.description.includes("Multiplayer")
  );

  const multiPlayer = data.games.filter((item) =>
    item.description.includes("Multiplayer")
  );

  return (
    <>
      <Helmet>
        <title>Leaderbored</title>
        <link
          rel="canonical"
          href="https://leaderbored.gg/"
        />
        <meta
          name="description"
          content="Page consisting of webcomics and free HTML5 games to play."
        />
      </Helmet>

      <Box color="white">
        {/* Existing homepage sections, kept hidden as before */}
        <div style={{ display: "none" }}>
          <Carousel games={carouselGames} />
          <GameBannerAd />

          <Box id="comics">
            <ComicPanel />
          </Box>

          <Box
            bgImage={taktLogo}
            bgPos="center"
            bgSize="cover"
            borderRadius="20px"
            mb="40px"
            bgRepeat="no-repeat"
          >
            <FilteredGames
              heading="Game of the week"
              games={[singlePlayer[0]]}
            />
          </Box>
        </div>

        <BlogsMini />

        <FilteredGames
          heading="Single player"
          games={singlePlayer}
          isPC={isLargerThan1580}
        />

        <GameBannerAd />

        <FilteredGames
          heading="Multiplayer"
          games={multiPlayer}
          isPC={isLargerThan1580}
        />

        <GameBannerAd />
        <GameBannerAd />
      </Box>
    </>
  );
}

function BlogsMini() {
  const [blogs, setBlogs] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadBlogs() {
      try {
        if (!API_KEY) {
          throw new Error(
            "Blogger API key is not configured."
          );
        }

        const params = new URLSearchParams({
          key: API_KEY,
          fetchImages: "true",
          maxResults: "10",
        });

        const response = await fetch(
          `https://www.googleapis.com/blogger/v3/blogs/${BLOG_ID}/posts?${params}`
        );

        if (!response.ok) {
          throw new Error("Could not load Blogger posts.");
        }

        const data = await response.json();

        if (!cancelled) {
          setBlogs(data.items || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Could not load news.");
        }
      }
    }

    loadBlogs();

    return () => {
      cancelled = true;
    };
  }, []);

  function processText(html, maxLength = 53) {
    const textOnly = document.createElement("div");
    textOnly.innerHTML = html || "";

    const text = (textOnly.textContent || "")
      .replace(/\s+/g, " ")
      .trim();

    return text.length > maxLength
      ? `${text.substring(0, maxLength).trim()}...`
      : text;
  }

  if (error) {
    console.error(error);
  }

  return (
    <Grid
      templateColumns={{
        base: "repeat(2, 1fr)",
        sm: "repeat(3, 1fr)",
        md: "repeat(4, 1fr)",
        lg: "repeat(5, 1fr)",
        xl: "repeat(6, 1fr)",
      }}
      gap={{ base: 4, md: 5, xl: 6 }}
      w="100%"
      maxW="100%"
      m={0}
      px={{ base: 2, md: 4 }}
      py={{ base: 2, md: 4 }}
      alignItems="stretch"
      justifyItems="stretch"
      mb="12px"
    >
      {blogs.map((blog) => {
        const title = blog.title || "Leaderbored News";
        const newsSlug = slugify(title);

        const image =
          blog.images?.[0]?.url ||
          blog.content?.match(
            /<img[^>]+src=["']([^"']+)["']/i
          )?.[1] ||
          placeholderThumbnail;

        return (
          <Box
            key={blog.id}
            as="a"
            href={`/news/${newsSlug}`}
            textDecoration="none"
            _hover={{ textDecoration: "none" }}
          >
            <HStack w="270px" align="start">
              <Image
                boxSize="60px"
                borderRadius="14px"
                src={image}
                loading="lazy"
                objectFit="contain"
                alt={title}
                fallbackSrc={placeholderThumbnail}
              />

              <Stack spacing="0px">
                <Text fontWeight="bold" fontSize="18px">
                  {title}
                </Text>

                <Text lineHeight="20px">
                  {processText(blog.content, 53)}
                </Text>
              </Stack>
            </HStack>
          </Box>
        );
      })}
    </Grid>
  );
}

export default Homepage;
